/**
 * @file CopyFromDropdown.js
 * @description A dropdown component for selecting a client to copy from.
 */

import React, { useState, useEffect } from "react";
import { getClients } from "../../../services/clientService";
import { Dropdown } from "../../helpers/Dropdown";
import LoadingWrapper from "../../ui/loaders/LoadingWrapper";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/**
 * Represents a dropdown component used for copying data from a selected client.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label should be displayed inline.
 * @param {string} props.selectedType - The ID of the selected client type.
 * @param {string} props.clientTypeId - The ID of the client type.
 * @param {Function} props.onTypeChange - The callback function triggered when the client type changes.
 * @param {string} [props.label="Copy From"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {boolean} [props.disabled=false] - Determines whether the dropdown is disabled.
 * @returns {JSX.Element} The rendered CopyFromDropdown component.
 */
const CopyFromDropdown = ({
  className,
  inlineLabel = true,
  selectedType,
  clientTypeId,
  onTypeChange,
  label = "Copy From",
  isRequired = false,
  disabled = false,
}) => {
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setIsLoading(true);
        const data = await getClients([clientTypeId]);
        setClients(data);
      } catch (error) {
        console.error("Could not fetch clients:", error);
        Toast.error(getText('msg_3ada0a47c537'));
      } finally {
        setIsLoading(false);
      }
    };

    if (clientTypeId) {
      fetchClients();
    } else {
      setClients([]);
      setIsLoading(false);
    }
  }, [clientTypeId]);

  const getDefaultLabel = () => {
    if (clients.length === 0 && !isLoading) {
      return "No Results";
    }
    const matchingClient = clients.find((client) => client.id === selectedType);
    return matchingClient ? matchingClient.name : "Select Client";
  };

  const clientOptions = clients.map((client) => ({
    value: client.id,
    label: client.name,
  }));

  const isDropdownDisabled = disabled || clients.length === 0;

  return (
    <div
      className={`form-group mb-3 ${
        inlineLabel ? "inline-input" : ""
      } ${className}`}
    >
      <label htmlFor="copy-from-dropdown">
        {label}
        {isRequired && <span className="text-danger"> *</span>}
      </label>
      <LoadingWrapper isLoading={isLoading}>
        <Dropdown
          id="copy-from-dropdown"
          defaultValue={getDefaultLabel()}
          options={clientOptions}
          value={selectedType}
          onChange={onTypeChange}
          disabled={isDropdownDisabled}
        />
      </LoadingWrapper>
    </div>
  );
};

export default CopyFromDropdown;
